import { Injectable } from '@angular/core';
import { finalize, from, Observable, shareReplay } from 'rxjs';
import { HttpClientService } from '../http-client/http-client.service';
import { LocalStorageService } from '../localStorage/local-storage.service';

export interface RefreshedTokens {
  bearerToken: string;
  refreshToken: string;
}

/**
 * Coordena a renovação do access token para que só exista UMA chamada de
 * /auth/refresh em voo por vez, tanto entre requisições 401 concorrentes na
 * mesma aba (shareReplay) quanto entre abas da mesma origem (Web Locks API) —
 * o backend faz rotação single-use do refresh token, então uma segunda
 * chamada concorrente com o token antigo sempre falharia.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshCoordinatorService {
  private inFlight$: Observable<RefreshedTokens> | null = null;

  constructor(
    private httpClientService: HttpClientService,
    private localStorage: LocalStorageService,
  ) {}

  refresh(staleBearerToken: string): Observable<RefreshedTokens> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = from(this.refreshViaLock(staleBearerToken)).pipe(
      shareReplay(1),
      finalize(() => {
        this.inFlight$ = null;
      }),
    );

    return this.inFlight$;
  }

  private async refreshViaLock(staleBearerToken: string): Promise<RefreshedTokens> {
    return navigator.locks.request('vivest-center:token-refresh', async () => {
      // Ao entrar no lock, outra aba pode já ter rotacionado o token enquanto
      // esperávamos — se o bearerToken atual já é diferente do que causou o 401
      // nesta aba, reaproveita o resultado da outra aba em vez de chamar a rede.
      const currentBearerToken = String(this.localStorage.get('bearerToken'));
      if (currentBearerToken && currentBearerToken !== staleBearerToken) {
        return {
          bearerToken: currentBearerToken,
          refreshToken: String(this.localStorage.get('refreshToken')),
        };
      }

      const refreshToken = String(this.localStorage.get('refreshToken'));
      const response: any = await this.httpClientService.refreshToken(refreshToken);
      const rotated: RefreshedTokens = {
        bearerToken: response.body.auth.token.value,
        refreshToken: response.body.auth.token.refresh,
      };

      this.localStorage.add('bearerToken', rotated.bearerToken);
      this.localStorage.add('refreshToken', rotated.refreshToken);

      return rotated;
    });
  }
}
